import { useCalculationStore } from "@/stores/calculationStore";
import { useMainStore } from "@/stores/mainStore";
import { useSocketStore } from "@/stores/socketStore";
import { useSpaceStore } from "@/stores/spaceStore";
import {
  computed,
  ComputedRef,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  toValue,
  watch
} from "vue";

export function useCalcInput(
  nodeId: Ref<string>,
  field: Ref<string>,
  ro: boolean,
  type: "text" | "number" = "text"
) {
  const socketStore = useSocketStore();
  const spaceStore = useSpaceStore();
  const calculationStore = useCalculationStore();
  const mainStore = useMainStore();

  const inputField = ref(null);
  const changedValue = ref(null);
  const debounceSend = ref(undefined);
  const debounceInactive = ref(undefined);

  const isOccupied = computed(() => {
    return mainStore.occupiedFields[nodeId.value]?.[field.value];
  });
  const error = computed(() => {
    return (
      spaceStore.fieldStatus.field[nodeId.value + "|" + field.value] == "error"
    );
  });
  const dataBaseValue = computed(() => {
    return spaceStore.calculationElements[nodeId.value][field.value];
  });

  const levelToLicence = {
    1: 1,
    2: 2,
    3: 4,
    undefined: 7
  };
  const readonly = computed(() => {
    return !!(
      ro ||
      isOccupied.value ||
      spaceStore.readOnly ||
      !(
        spaceStore.currentSpace.licence &
        levelToLicence[spaceStore.getElement(nodeId.value).level]
      )
    );
  });

  onMounted(() => {
    changedValue.value =
      spaceStore.calculationElements[nodeId.value]?.[field.value];
  });

  onUnmounted(() => {
    clearTimeout(debounceSend.value);
    clearTimeout(debounceInactive.value);
    debounceSend.value && saveValue(changedValue.value);
  });

  watch(dataBaseValue, (value) => {
    if (value !== changedValue.value) {
      changedValue.value = value;
    }
  });

  watch(
    () => nodeId.value,
    (newId) => {
      // Reset field on id change [reset creation form]
      changedValue.value = spaceStore.calculationElements[newId][field.value];
    }
  );

  function saveValue(newValue: string) {
    if (spaceStore.readOnly) {
      return;
    }
    const calculationStore = useCalculationStore();
    console.log("[CalcInput]", "[DEBUG]", "Value send", newValue);

    debounceSend.value = undefined;
    calculationStore.changeValue(nodeId.value, field.value, newValue);
  }

  function resetInactiveDebounce() {
    clearTimeout(debounceInactive.value);
    debounceInactive.value = setTimeout(() => {
      // Disable Focus from element
      // clear Field for other users
      inputField.value &&
        typeof inputField.value.blur === "function" &&
        inputField.value.blur();
    }, 10000);
  }

  function watchFocus(value: boolean) {
    console.log("Focus changed to", value, readonly.value);
    if (readonly.value) {
      return;
    }

    if (spaceStore.readOnly) {
      return;
    }

    if (value) {
      onFocus();
    } else if (!value) {
      onBlur();
    }
  }

  function onFocus() {
    if (spaceStore.readOnly) {
      return;
    }
    socketStore.socket.emit("element-focus", nodeId.value, field.value);
    calculationStore.focusElement(nodeId.value, field.value);
    resetInactiveDebounce();
  }

  function onBlur() {
    if (spaceStore.readOnly) {
      return;
    }
    socketStore.socket.emit("element-blur", nodeId.value, field.value);

    // Later put back in, but for now calculated even without change
    // debounceSend.value && saveValue(changedValue.value);
    clearTimeout(debounceSend.value);
    saveValue(changedValue.value);
  }

  function isNumber(evt: KeyboardEvent) {
    console.log("evt.key:", evt.key, evt, evt.key.length);
    if (evt.key.length > 1) {
      return true;
    }

    if (evt.key === ",") {
      evt.preventDefault();
      value.value += ".";
      return;
    }

    if (evt.key === ".") {
      if (value.value.includes(".")) {
        evt.preventDefault();
        return;
      }
      return;
    }

    if (evt.key === "e") {
      if (value.value.includes("e")) {
        evt.preventDefault();
      }
      return true;
    }

    if (
      !["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-"].includes(evt.key)
    ) {
      evt.preventDefault();
      return;
    }
  }

  const value = computed({
    get() {
      if (readonly.value) {
        return spaceStore.calculationElements[nodeId.value][field.value];
      }
      return changedValue.value;
    },
    set(newValue) {
      if (spaceStore.readOnly) {
        return;
      }
      console.log("newValue:", newValue);
      // TODO Validate input and have none calculation fields extra

      if (type === "number") {
        if (isNaN(Number(newValue))) {
          console.log("newValue: Invalid number");
          return;
        }

        changedValue.value = newValue.split(",").join("."); // Prevent errors with "," in Numbers
      } else {
        changedValue.value = newValue;
      }

      socketStore.socket.emit(
        "field-input",
        nodeId.value,
        field.value,
        changedValue.value
      );

      calculationStore.calculateOnlyCurrent(
        nodeId.value,
        field.value,
        changedValue.value
      );

      clearTimeout(debounceSend.value);
      debounceSend.value = setTimeout(() => {
        saveValue(changedValue.value);
      }, 5000);

      resetInactiveDebounce();
    }
  });

  return {
    inputField,
    debounceSend,
    debounceInactive,
    changedValue,
    isOccupied,
    error,
    dataBaseValue,
    saveValue,
    watchFocus,
    onFocus,
    onBlur,
    readonly,
    resetInactiveDebounce,
    value,
    isNumber: type === "number" ? isNumber : () => {}
  };
}
